'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  FileText,
  Loader2,
  Sparkles,
  ZoomIn
} from 'lucide-react';

import { API_ENDPOINTS } from '@/lib/api/config';
import { cn } from '@/lib/utils';
import {
  prefetchAuthenticatedBlob,
  useAuthenticatedBlobUrl
} from '@/hooks/use-authenticated-blob-url';
import { usePanelWidth } from '@/hooks/use-panel-width';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  ocrTemplateKeys,
  ocrTemplatesApi,
  TemplateFieldsInfo,
  type OcrTemplate
} from '@/features/ocr-templates';
import { toast } from 'sonner';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import type { InfiniteSelectConfig } from '@/types/base-form';

import { usePageTemplateSuggestionsLive } from '../hooks/use-page-template-suggestions-live';
import {
  useRegeneratePageSuggestionsMutation,
  useRerunOcrPagesMutation,
  useRetryPageSuggestionMutation,
  useStartOcrMutation
} from '../services/ocr.mutations';
import {
  ocrQueries,
  pageSuggestionPollingTimedOut,
  resetPageSuggestionPollWindow,
  resetSinglePageSuggestionPollWindow,
  singlePageSuggestionPollingTimedOut
} from '../services/ocr.queries';
import { useOcrToast } from '../utils/ocr-toast';
import {
  selectAllState,
  selectedAssignments,
  toggleAllPages,
  togglePage
} from '../utils/page-selection';
import {
  initialPageMode,
  pageSuggestionsState,
  restoredSuggestions,
  resolvePageSuggestions,
  resolvePageTemplate,
  type PageMode,
  type PageRequestInput
} from '../utils/page-suggestions';
import type { OcrPagePreview, PageTemplateSuggestion } from '../types';
import { OcrPageSuggestionList } from './ocr-page-suggestion-list';
import { DocumentPagesViewer, type ViewerPage } from './document-pages-viewer';

/** Stable empty highlight list — the preview draws no regions. */
const NO_CHUNKS: string[] = [];

interface OcrPerPageAssignmentProps {
  containerId: number;
  registrationId: number;
  containerLabel?: string;
  /**
   * The container already has a run: re-run only the pages the operator ticks,
   * each with its own template, and keep every other page's extraction
   * (OMA-674). Off for a first start, where every page is sent and every page
   * needs a template.
   */
  rerunExistingRun?: boolean;
  /** Called after OCR is successfully started so the parent can close. */
  onStarted: () => void;
}

export function OcrPerPageAssignment({
  containerId,
  containerLabel,
  rerunExistingRun = false,
  onStarted
}: OcrPerPageAssignmentProps) {
  const t = useTranslations('ocr');
  const toasts = useOcrToast();

  // Two modes per page: Auto offers the page's ranked template suggestions
  // (rank 1 pre-selected), Manual picks from the full catalog. Pages render
  // immediately from the cheap pages endpoint. The AI ranking is OPT-IN: it
  // costs a model call per page, so it starts only when the operator presses
  // "suggest templates" (every page) or "Auto" on one row (that page only) —
  // never by opening the tab. Either runs in a queued job; the queries poll
  // (and the socket pushes) while pending. The container run reuses pages
  // already ranked one at a time, server-side.
  const pagesQuery = useQuery(ocrQueries.containerPages(containerId));
  // A ranking survives a reload in the server cache; this read finds it
  // (and the pages ranked one at a time) without ever starting one.
  const cachedSuggestionsQuery = useQuery(
    ocrQueries.cachedPageTemplateSuggestions(containerId)
  );
  const [pagesRegenerated, setPagesRegenerated] = useState(false);
  const restored = restoredSuggestions(cachedSuggestionsQuery.data?.data, {
    dropPages: pagesRegenerated
  });
  const [suggestionsAsked, setSuggestionsAsked] = useState(false);
  const suggestionsRequested = suggestionsAsked || restored.rankingCached;
  const suggestionsQuery = useQuery(
    ocrQueries.pageTemplateSuggestions(containerId, suggestionsRequested)
  );
  const regenerateMutation = useRegeneratePageSuggestionsMutation();
  const suggestionsPayload = suggestionsQuery.data?.data;
  // Only a genuinely pending ranking spins. A request error, the backend's
  // cached failure ('ready' with no pages) or a poll window that ran out are
  // terminal: every page falls back to Manual.
  const suggestionsState = pageSuggestionsState({
    requested: suggestionsRequested,
    // `none` only ever answers the cache-only read, never this query.
    status:
      suggestionsPayload?.status === 'none'
        ? undefined
        : suggestionsPayload?.status,
    pageCount: suggestionsPayload?.pages?.length ?? 0,
    isError: suggestionsQuery.isError,
    pollTimedOut:
      !suggestionsQuery.isFetching && pageSuggestionPollingTimedOut(containerId)
  });
  const suggestionsPending =
    suggestionsState === 'pending' || regenerateMutation.isPending;

  // Pages ranked on demand, one query each.
  const [askedPageRequests, setPageRequests] = useState<number[]>([]);
  const pageRequests = [
    ...new Set([...restored.pageIds, ...askedPageRequests])
  ];
  const pageQueries = useQueries({
    queries: pageRequests.map((pageId) =>
      ocrQueries.pageTemplateSuggestion(containerId, pageId)
    )
  });
  const retryPageMutation = useRetryPageSuggestionMutation();

  // The poll window is module-level and outlives the dialog, so every request
  // starts a fresh one — a window that ran out earlier would otherwise read as
  // a failure the moment the operator asks again.
  const requestSuggestions = () => {
    resetPageSuggestionPollWindow(containerId);
    if (!suggestionsRequested) {
      setSuggestionsAsked(true);
      return;
    }
    // Keep it requested even if the restored ranking is about to be cleared.
    setSuggestionsAsked(true);
    setPagesRegenerated(true);
    // Re-run (after a ranking or a failure): drop the cached result — page
    // entries included, server-side — and rank again, so edited templates or
    // a recovered endpoint are picked up. Page requests are dropped too, or
    // their queries would refetch the cleared entries and queue a second,
    // duplicate job per page beside the container one.
    setPageRequests([]);
    regenerateMutation.mutate(containerId);
  };
  // Never auto-select from a failed payload.
  const suggestionsByPage: Record<number, PageTemplateSuggestion[]> =
    suggestionsState === 'ready' ? (suggestionsPayload?.suggestions ?? {}) : {};

  const pageRequestFor = (pageId: number): PageRequestInput => {
    const index = pageRequests.indexOf(pageId);
    if (index < 0) return { requested: false };
    const query = pageQueries[index];
    const payload = query?.data?.data;
    return {
      requested: true,
      status: payload?.status,
      failed: payload?.failed ?? false,
      suggestions: payload?.suggestions ?? [],
      isError: query?.isError ?? false,
      pollTimedOut:
        !query?.isFetching && singlePageSuggestionPollingTimedOut(pageId)
    };
  };
  const pageSourceFor = (pageId: number) =>
    resolvePageSuggestions({
      containerState: suggestionsState,
      containerSuggestions: suggestionsByPage[pageId],
      page: pageRequestFor(pageId)
    });
  const pendingFor = (pageId: number): boolean =>
    pageSourceFor(pageId).state === 'pending' ||
    (retryPageMutation.isPending &&
      retryPageMutation.variables?.pageId === pageId);

  const anyPagePending = pageRequests.some((pageId) => pendingFor(pageId));
  usePageTemplateSuggestionsLive(
    containerId,
    suggestionsPending || anyPagePending
  );

  // Active templates for the manual picker — infinite scroll + server search,
  // so the list is never capped. cmdk's own filter is disabled; the backend
  // does the searching. One shared source feeds every per-page picker.
  const templatesSelect = useInfiniteSelect<OcrTemplate>({
    queryKey: ocrTemplateKeys.lists(),
    queryFn: (params) => ocrTemplatesApi.getAll(params),
    mapOption: useCallback(
      (tpl: OcrTemplate) => ({ value: String(tpl.id), label: tpl.name }),
      []
    ),
    params: { is_active: true }
  });

  const startMutation = useStartOcrMutation();
  const rerunPagesMutation = useRerunOcrPagesMutation();

  const pages = useMemo<OcrPagePreview[]>(
    () => pagesQuery.data?.data?.pages ?? [],
    [pagesQuery.data]
  );

  // The container form's configured template, surfaced by the pages endpoint.
  // Pre-selected for every page below; null when the form has none. Also used
  // as a name fallback so the trigger shows the right label even before the
  // default's page is loaded into the picker.
  const defaultTemplate = pagesQuery.data?.data?.default_template ?? null;
  const defaultTemplateId = defaultTemplate?.id ?? null;

  // The page whose full-size preview dialog is open (`null` = closed). Tracked
  // by page id (not index) so it survives a refetch that reorders the set.
  const [previewPageId, setPreviewPageId] = useState<number | null>(null);
  const previewIndex = useMemo(
    () => pages.findIndex((page) => page.id === previewPageId),
    [pages, previewPageId]
  );

  // Only the full-body spinner when there is nothing to show yet.
  const isLoading = pages.length === 0 && pagesQuery.isPending;

  // Per-page operator state: the catalog pick (Manual), the chosen suggestion
  // (Auto, defaults to rank 1) and an explicit mode. A page with no explicit
  // mode opens in Auto only when it has a ranking.
  const [manualPicks, setManualPicks] = useState<Record<number, number>>({});
  const [autoChoices, setAutoChoices] = useState<Record<number, number>>({});
  const [modes, setModes] = useState<Record<number, PageMode>>({});

  const suggestionsFor = (pageId: number): PageTemplateSuggestion[] =>
    pageSourceFor(pageId).suggestions;
  // Auto stays shown while the page the operator switched is still ranking;
  // otherwise a page with no ranking is Manual, whatever was toggled before.
  const modeFor = (pageId: number): PageMode => {
    if (modes[pageId] === 'auto' && pendingFor(pageId)) return 'auto';
    const suggestions = suggestionsFor(pageId);
    if (suggestions.length === 0) return 'manual';
    return modes[pageId] ?? initialPageMode(suggestions);
  };
  // Why a page the operator switched to Auto came back to Manual.
  const noticeFor = (pageId: number): string | null => {
    if (modes[pageId] !== 'auto' || pendingFor(pageId)) return null;
    const source = pageSourceFor(pageId);
    if (source.state === 'failed') return t('pageSuggestionFailed');
    if (source.state === 'ready' && source.suggestions.length === 0) {
      return t('pageNoSuggestion');
    }
    return null;
  };

  // Falls back to the container's configured default template (null =
  // unassigned, operator must pick).
  const resolvedTemplateId = (pageId: number): number | null =>
    resolvePageTemplate({
      mode: modeFor(pageId),
      suggestions: suggestionsFor(pageId),
      autoChoice: autoChoices[pageId],
      manualPick: manualPicks[pageId],
      defaultTemplateId
    });

  const assignedCount = pages.filter(
    (p) => resolvedTemplateId(p.id) != null
  ).length;
  const allAssigned = pages.length > 0 && assignedCount === pages.length;

  // Re-run mode: which pages to re-read. Starts empty — the operator says
  // which pages are wrong; nothing is re-extracted by default.
  const [selectedPages, setSelectedPages] = useState<Set<number>>(
    () => new Set()
  );
  const pageIds = pages.map((page) => page.id);
  const selectedCount = pageIds.filter((id) => selectedPages.has(id)).length;
  const rerunPayload = rerunExistingRun
    ? selectedAssignments(pageIds, selectedPages, resolvedTemplateId)
    : null;
  const canRun = rerunExistingRun ? rerunPayload != null : allAssigned;
  const isRunning = startMutation.isPending || rerunPagesMutation.isPending;

  const setManualTemplate = (pageId: number, templateId: number) => {
    setManualPicks((prev) => ({ ...prev, [pageId]: templateId }));
  };
  const setAutoChoice = (pageId: number, templateId: number) => {
    setAutoChoices((prev) => ({ ...prev, [pageId]: templateId }));
  };
  const setMode = (pageId: number, mode: PageMode) => {
    setModes((prev) => ({ ...prev, [pageId]: mode }));
  };

  // "Auto" on one row ranks THAT page when nothing has ranked it yet — unless
  // the container-wide ranking is already running, which covers it.
  const changeMode = (pageId: number, mode: PageMode) => {
    setMode(pageId, mode);
    if (mode !== 'auto') return;
    const source = pageSourceFor(pageId);
    if (source.suggestions.length > 0 || source.state === 'pending') return;

    resetSinglePageSuggestionPollWindow(pageId);
    if (!pageRequests.includes(pageId)) {
      setPageRequests((prev) => [...prev, pageId]);
      return;
    }
    // Asked before and it failed (or matched nothing): rank it again.
    retryPageMutation.mutate({ containerId, pageId });
  };

  const handleRun = async () => {
    if (!canRun) return;
    try {
      if (rerunExistingRun && rerunPayload) {
        await rerunPagesMutation.mutateAsync({
          containerId,
          pages: rerunPayload
        });
        toast.success(t('rerunPagesStarted', { count: rerunPayload.length }));
        onStarted();
        return;
      }

      await startMutation.mutateAsync({
        containerId,
        pageTemplateAssignments: pages.map((page) => ({
          page_id: page.id,
          ocr_template_id: resolvedTemplateId(page.id) as number,
          mode: modeFor(page.id)
        }))
      });
      toasts.started(containerId, containerLabel);
      onStarted();
    } catch {
      // handled by global MutationCache
    }
  };

  return (
    <div className='flex min-h-0 flex-col gap-3'>
      <div className='flex items-center justify-between gap-2'>
        <p className='text-muted-foreground text-xs'>
          {rerunExistingRun ? t('rerunPagesHint') : t('assignPerPageHint')}
        </p>
        <div className='flex shrink-0 items-center gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={requestSuggestions}
            disabled={pages.length === 0 || suggestionsPending}
          >
            {suggestionsPending ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : (
              <Sparkles className='me-2 h-4 w-4' />
            )}
            {suggestionsPending
              ? t('suggestingTemplates')
              : suggestionsState === 'ready'
                ? t('resuggestTemplates')
                : suggestionsState === 'failed'
                  ? t('retrySuggestions')
                  : t('suggestTemplates')}
          </Button>
          <Badge variant='outline'>
            {rerunExistingRun
              ? t('pagesSelected', {
                  count: selectedCount,
                  total: pages.length
                })
              : t('pagesAssigned', {
                  count: assignedCount,
                  total: pages.length
                })}
          </Badge>
        </div>
      </div>
      {rerunExistingRun && pages.length > 0 ? (
        <label className='flex w-fit cursor-pointer items-center gap-2 px-3 text-sm'>
          <Checkbox
            checked={selectAllState(selectedPages, pageIds)}
            onCheckedChange={(value) =>
              setSelectedPages(toggleAllPages(pageIds, value === true))
            }
            aria-label={t('selectAllPages')}
          />
          {t('selectAllPages')}
        </label>
      ) : null}
      {suggestionsState === 'failed' ? (
        <p className='text-muted-foreground text-xs'>
          {t('autoSuggestionsUnavailable')}
        </p>
      ) : null}

      {isLoading ? (
        <div className='text-muted-foreground flex items-center gap-2 py-10 text-sm'>
          <Loader2 className='h-4 w-4 animate-spin' />
          {t('analyzingPages')}
        </div>
      ) : pages.length === 0 ? (
        <p className='text-muted-foreground py-10 text-center text-sm'>
          {t('noPagesToAssign')}
        </p>
      ) : (
        <ScrollArea className='h-[50vh] rounded-md border'>
          <ul className='divide-y'>
            {pages.map((page, index) => (
              <PageAssignmentRow
                key={page.id}
                page={page}
                index={index}
                onPreview={() => setPreviewPageId(page.id)}
                value={resolvedTemplateId(page.id)}
                mode={modeFor(page.id)}
                suggestions={suggestionsFor(page.id)}
                suggestionsPending={pendingFor(page.id)}
                notice={noticeFor(page.id)}
                onModeChange={(mode) => changeMode(page.id, mode)}
                onPickSuggestion={(id) => setAutoChoice(page.id, id)}
                templatesConfig={templatesSelect}
                defaultTemplate={defaultTemplate}
                onPickManual={(id) => setManualTemplate(page.id, id)}
                selection={
                  rerunExistingRun
                    ? {
                        checked: selectedPages.has(page.id),
                        onChange: (checked) =>
                          setSelectedPages((prev) =>
                            togglePage(prev, page.id, checked)
                          )
                      }
                    : undefined
                }
              />
            ))}
          </ul>
        </ScrollArea>
      )}

      <div className='flex justify-end'>
        <Button onClick={handleRun} disabled={!canRun || isRunning}>
          {isRunning && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
          {rerunExistingRun
            ? t('rerunSelectedPages', { count: selectedCount })
            : t('runWithAssignments')}
        </Button>
      </div>

      {previewPageId != null && previewIndex >= 0 ? (
        <PageTemplatePreviewDialog
          pages={pages}
          index={previewIndex}
          onIndexChange={(i) => setPreviewPageId(pages[i]?.id ?? null)}
          onClose={() => setPreviewPageId(null)}
          templatesConfig={templatesSelect}
          defaultTemplate={defaultTemplate}
          valueFor={resolvedTemplateId}
          modeFor={modeFor}
          suggestionsFor={suggestionsFor}
          pendingFor={pendingFor}
          noticeFor={noticeFor}
          onModeChange={changeMode}
          onPickSuggestion={setAutoChoice}
          onPickManual={setManualTemplate}
        />
      ) : null}
    </div>
  );
}

interface DefaultTemplate {
  id: number;
  name: string;
}

interface PageTemplatePreviewDialogProps {
  pages: OcrPagePreview[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  templatesConfig: InfiniteSelectConfig;
  defaultTemplate: DefaultTemplate | null;
  valueFor: (pageId: number) => number | null;
  modeFor: (pageId: number) => PageMode;
  suggestionsFor: (pageId: number) => PageTemplateSuggestion[];
  pendingFor: (pageId: number) => boolean;
  noticeFor: (pageId: number) => string | null;
  onModeChange: (pageId: number, mode: PageMode) => void;
  onPickSuggestion: (pageId: number, templateId: number) => void;
  onPickManual: (pageId: number, templateId: number) => void;
}

/**
 * Full-size page preview in an overlay dialog. Shows the high-resolution page
 * image alongside that page's template selection, so the operator can review
 * the document and assign the right template without leaving the preview.
 * Prev/next steps through the pages and warms the neighbours for instant nav.
 */
function PageTemplatePreviewDialog({
  pages,
  index,
  onIndexChange,
  onClose,
  templatesConfig,
  defaultTemplate,
  valueFor,
  modeFor,
  suggestionsFor,
  pendingFor,
  noticeFor,
  onModeChange,
  onPickSuggestion,
  onPickManual
}: PageTemplatePreviewDialogProps) {
  const t = useTranslations('ocr');
  const page = pages[index];
  const total = pages.length;

  const viewerPages = useMemo<ViewerPage[]>(
    () =>
      page
        ? [
            {
              imageUrl: null,
              authEndpoint: API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(
                page.workload_assignment_id,
                page.id
              ),
              label: t('pageLabel', { number: index + 1 }),
              chunks: [],
              isProcessing: false
            }
          ]
        : [],
    [page, index, t]
  );

  const assignPanelResize = usePanelWidth('ocr-page-assign', {
    defaultWidth: 340,
    min: 280,
    max: 560,
    // Sits at the split's inline end, so its handle is on its inline-start
    // edge — physically left in LTR, right in RTL.
    edge: 'start'
  });

  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      const neighbour = pages[i];
      if (neighbour) {
        prefetchAuthenticatedBlob(
          API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(
            neighbour.workload_assignment_id,
            neighbour.id
          )
        );
      }
    });
  }, [index, pages]);

  if (!page) return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className='flex h-[96dvh] max-h-[96dvh] w-[98vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[110rem]'>
        <DialogHeader className='border-b px-4 py-3'>
          {/* Reserve inline-end space so the page-nav buttons never collide
              with the dialog's auto-rendered close (×) in the corner. */}
          <div className='flex items-center justify-between gap-2 pe-10'>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              disabled={index <= 0}
              onClick={() => onIndexChange(index - 1)}
              aria-label={t('prevPage')}
            >
              <ChevronLeft className='h-4 w-4 rtl:rotate-180' />
            </Button>
            <DialogTitle className='text-sm font-medium'>
              {t('pageOf', { current: index + 1, total })}
            </DialogTitle>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              disabled={index >= total - 1}
              onClick={() => onIndexChange(index + 1)}
              aria-label={t('nextPage')}
            >
              <ChevronRight className='h-4 w-4 rtl:rotate-180' />
            </Button>
          </div>
          <DialogDescription className='sr-only'>
            {t('assignPerPageHint')}
          </DialogDescription>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col lg:flex-row'>
          {/* The shared viewer, not a bare <img>: zoom, fit, pan and
              fullscreen, so a dense scan can be read before a template is
              picked for it (OMA-672). Keyed by page so each step through the
              pages opens at the default fit instead of the last page's zoom.
              Read-only — no highlight, rotate or rearrange props. */}
          <div className='bg-muted/30 min-h-0 flex-1 p-2'>
            <DocumentPagesViewer
              key={page.id}
              pages={viewerPages}
              pdfUrl={null}
              highlightedChunkIds={NO_CHUNKS}
              activeChunkIds={NO_CHUNKS}
              activePage={0}
            />
          </div>
          <PanelResizeHandle {...assignPanelResize.handleProps} />
          <div
            // `w-full` covers the stacked layout below `lg`, where there is
            // no adjacent column and the panel spans the dialog. The local
            // variable only takes effect once the split is side-by-side.
            className='w-full shrink-0 space-y-3 overflow-y-auto border-t p-4 lg:w-(--assign-panel-width) lg:border-s lg:border-t-0'
            style={
              {
                // Distinct from the hook's own --panel-ocr-page-assign-width:
                // a property cannot reference itself, CSS treats that as a
                // cycle and drops the declaration.
                '--assign-panel-width': assignPanelResize.widthValue
              } as React.CSSProperties
            }
          >
            <div>
              <p className='text-sm font-medium'>{t('templateForThisPage')}</p>
              <p className='text-muted-foreground truncate text-xs'>
                {page.page_number}
              </p>
            </div>
            <PageTemplateControls
              layout='panel'
              value={valueFor(page.id)}
              mode={modeFor(page.id)}
              suggestions={suggestionsFor(page.id)}
              suggestionsPending={pendingFor(page.id)}
              notice={noticeFor(page.id)}
              onModeChange={(mode) => onModeChange(page.id, mode)}
              onPickSuggestion={(id) => onPickSuggestion(page.id, id)}
              templatesConfig={templatesConfig}
              defaultTemplate={defaultTemplate}
              onPickManual={(id) => onPickManual(page.id, id)}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PageTemplateControlsProps {
  /** 'row' = compact inline (list); 'panel' = stacked (preview dialog). */
  layout: 'row' | 'panel';
  value: number | null;
  mode: PageMode;
  /** This page's ranked suggestions, best first; empty = no ranking. */
  suggestions: PageTemplateSuggestion[];
  /** This page's ranking is still running (spinner on Auto). */
  suggestionsPending: boolean;
  /** Why Auto fell back to Manual for this page, if it did. */
  notice?: string | null;
  onModeChange: (mode: PageMode) => void;
  onPickSuggestion: (templateId: number) => void;
  templatesConfig: InfiniteSelectConfig;
  defaultTemplate: DefaultTemplate | null;
  onPickManual: (templateId: number) => void;
}

/**
 * The mode toggle plus the page's selection. Auto shows the page's ranked
 * suggestions (rank 1 pre-selected) — choosing it on a page nothing has
 * ranked yet starts ranking that page; Manual picks from the full catalog.
 * Shared by the list row and the preview dialog so both selection surfaces
 * stay in lockstep.
 */
function PageTemplateControls({
  layout,
  value,
  mode,
  suggestions,
  suggestionsPending,
  notice,
  onModeChange,
  onPickSuggestion,
  templatesConfig,
  defaultTemplate,
  onPickManual
}: PageTemplateControlsProps) {
  const t = useTranslations('ocr');

  const nameOf = (templateId: number | null): string | null => {
    if (templateId == null) return null;
    const loaded = templatesConfig.options.find(
      (opt) => Number(opt.value) === templateId
    )?.label;
    if (loaded) return loaded;
    // The configured default may not be in the loaded pages yet — fall back to
    // its name so the trigger still shows the right label.
    if (defaultTemplate && defaultTemplate.id === templateId) {
      return defaultTemplate.name;
    }
    return null;
  };

  const toggle = (
    <ToggleGroup
      type='single'
      size='sm'
      value={mode}
      onValueChange={(next) => {
        if (next === 'auto' || next === 'manual') onModeChange(next);
      }}
      variant='outline'
      className={layout === 'panel' ? 'self-start' : undefined}
    >
      <ToggleGroupItem
        value='auto'
        disabled={suggestionsPending}
        className='gap-1.5'
      >
        {suggestionsPending ? (
          <Loader2 className='h-3 w-3 animate-spin' />
        ) : null}
        {t('auto')}
      </ToggleGroupItem>
      <ToggleGroupItem value='manual'>{t('manual')}</ToggleGroupItem>
    </ToggleGroup>
  );

  const selection =
    mode === 'auto' ? (
      suggestionsPending && suggestions.length === 0 ? (
        <p className='text-muted-foreground flex items-center gap-2 py-1.5 text-xs'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          {t('suggestingTemplates')}
        </p>
      ) : (
        <OcrPageSuggestionList
          suggestions={suggestions}
          value={value}
          onSelect={onPickSuggestion}
        />
      )
    ) : (
      <div className='flex w-full flex-col items-end gap-1'>
        <TemplatePicker
          value={value}
          valueName={nameOf(value)}
          infiniteConfig={templatesConfig}
          onSelect={onPickManual}
          placeholder={t('pickTemplate')}
          searchPlaceholder={t('searchTemplates')}
          allHeading={t('allTemplates')}
          emptyLabel={t('noTemplates')}
        />
        {notice ? (
          <p className='text-muted-foreground text-xs'>{notice}</p>
        ) : null}
      </div>
    );

  if (layout === 'row') {
    return (
      <>
        {toggle}
        {/* Auto lists several ranked names with score + fields beside each,
            so it needs more room than the single-line catalog picker. */}
        <div
          className={cn(
            'flex shrink-0 justify-end',
            mode === 'auto' ? 'w-[480px] max-w-[55%]' : 'w-[300px]'
          )}
        >
          {selection}
        </div>
      </>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {toggle}
      <div>{selection}</div>
    </div>
  );
}

interface PageAssignmentRowProps {
  page: OcrPagePreview;
  index: number;
  onPreview: () => void;
  value: number | null;
  mode: PageMode;
  suggestions: PageTemplateSuggestion[];
  suggestionsPending: boolean;
  notice: string | null;
  onModeChange: (mode: PageMode) => void;
  onPickSuggestion: (templateId: number) => void;
  templatesConfig: InfiniteSelectConfig;
  defaultTemplate: DefaultTemplate | null;
  onPickManual: (templateId: number) => void;
  /** Re-run mode only: whether this page is ticked for re-extraction. */
  selection?: { checked: boolean; onChange: (checked: boolean) => void };
}

function PageAssignmentRow({
  page,
  index,
  onPreview,
  value,
  mode,
  suggestions,
  suggestionsPending,
  notice,
  onModeChange,
  onPickSuggestion,
  templatesConfig,
  defaultTemplate,
  onPickManual,
  selection
}: PageAssignmentRowProps) {
  const t = useTranslations('ocr');

  // Page image via the authenticated thumbnail route (rasterizes PDF pages);
  // one blob fetch per row, cached/deduped by the hook.
  const thumbUrl = useAuthenticatedBlobUrl(
    API_ENDPOINTS.PHOTO_SCANNING.PAGE_THUMBNAIL(
      page.workload_assignment_id,
      page.id
    )
  );

  return (
    <li
      className={cn(
        'flex items-center gap-3 px-3 py-2.5',
        selection?.checked && 'bg-primary/5'
      )}
    >
      {selection ? (
        <Checkbox
          checked={selection.checked}
          onCheckedChange={(value) => selection.onChange(value === true)}
          aria-label={t('selectPageForRerun', { number: index + 1 })}
        />
      ) : null}
      <button
        type='button'
        onClick={onPreview}
        aria-label={t('openPagePreview')}
        className='bg-muted group focus-visible:ring-ring relative flex h-28 w-20 shrink-0 items-center justify-center overflow-hidden rounded border outline-none focus-visible:ring-2'
      >
        {thumbUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbUrl}
            alt={t('pageLabel', { number: index + 1 })}
            className='h-full w-full object-cover'
            loading='lazy'
            decoding='async'
          />
        ) : (
          <FileText className='text-muted-foreground/50 h-5 w-5' />
        )}
        <span className='bg-foreground/40 absolute inset-0 flex items-center justify-center text-white opacity-0 transition-opacity group-hover:opacity-100'>
          <ZoomIn className='h-5 w-5' />
        </span>
      </button>

      <div className='min-w-0 flex-1'>
        <p className='text-sm font-medium'>
          {t('pageLabel', { number: index + 1 })}
        </p>
        <p className='text-muted-foreground truncate text-xs'>
          {page.page_number}
        </p>
      </div>

      <PageTemplateControls
        layout='row'
        value={value}
        mode={mode}
        suggestions={suggestions}
        suggestionsPending={suggestionsPending}
        notice={notice}
        onModeChange={onModeChange}
        onPickSuggestion={onPickSuggestion}
        templatesConfig={templatesConfig}
        defaultTemplate={defaultTemplate}
        onPickManual={onPickManual}
      />
    </li>
  );
}

interface TemplatePickerProps {
  value: number | null;
  valueName: string | null;
  infiniteConfig: InfiniteSelectConfig;
  onSelect: (templateId: number) => void;
  placeholder: string;
  searchPlaceholder: string;
  allHeading: string;
  emptyLabel: string;
}

function TemplatePicker({
  value,
  valueName,
  infiniteConfig,
  onSelect,
  placeholder,
  searchPlaceholder,
  allHeading,
  emptyLabel
}: TemplatePickerProps) {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fetchGuardRef = useRef(false);

  const select = (id: number) => {
    onSelect(id);
    setOpen(false);
  };

  // Reset the server search when the popover closes (the source is shared
  // across all per-page pickers).
  useEffect(() => {
    if (!open) infiniteConfig.onSearchChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!infiniteConfig.isFetchingNextPage) fetchGuardRef.current = false;
  }, [infiniteConfig.isFetchingNextPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || fetchGuardRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (
      scrollHeight - scrollTop - clientHeight < 80 &&
      infiniteConfig.hasNextPage
    ) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [infiniteConfig]);

  return (
    // `modal` so the dropdown can scroll: this combobox lives inside a Dialog
    // whose scroll-lock (react-remove-scroll) otherwise blocks wheel events on
    // the portaled popover content.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='sm'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between'
        >
          <span className='truncate'>{valueName ?? placeholder}</span>
          <span className='ms-2 flex shrink-0 items-center gap-1'>
            {value != null ? <TemplateFieldsInfo templateId={value} /> : null}
            <ChevronsUpDown className='h-4 w-4 opacity-50' />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[280px] p-0' align='end'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={infiniteConfig.search}
            onValueChange={infiniteConfig.onSearchChange}
          />
          <div
            ref={scrollRef}
            className='max-h-[300px] overflow-y-auto'
            onScroll={handleScroll}
          >
            <CommandList className='max-h-none overflow-visible'>
              {infiniteConfig.isLoading ? (
                <div className='text-muted-foreground flex items-center justify-center gap-2 py-4 text-xs'>
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                </div>
              ) : (
                <>
                  <CommandEmpty>{emptyLabel}</CommandEmpty>
                  <CommandGroup heading={allHeading}>
                    {infiniteConfig.options.map((opt) => {
                      const id = Number(opt.value);
                      return (
                        <CommandItem
                          key={opt.value}
                          value={opt.value}
                          onSelect={() => select(id)}
                        >
                          <span className='truncate'>{opt.label}</span>
                          <span className='ms-auto flex items-center gap-1.5'>
                            <TemplateFieldsInfo templateId={id} />
                            {value === id ? (
                              <Check className='h-4 w-4' />
                            ) : null}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  {infiniteConfig.isFetchingNextPage && (
                    <div className='flex items-center justify-center py-2'>
                      <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
